import React, { useState, useEffect } from 'react';
import {
  Typography,
  Box,
  Container,
  Paper,
  Tabs,
  Tab,
  Button,
  TextField,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Divider,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Tooltip,
  Chip,
  Alert,
  Snackbar,
  Card,
  CardContent,
  CardActions,
  SelectChangeEvent,
  Stack
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import LinkIcon from '@mui/icons-material/Link';
import HistoryIcon from '@mui/icons-material/History';
import AppsIcon from '@mui/icons-material/Apps';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { db } from '../firebase/config';
import { 
  collection, 
  doc, 
  getDocs, 
  getDoc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  serverTimestamp, 
  query, 
  orderBy,
  collectionGroup,
  setDoc
} from 'firebase/firestore';
import { techStacks, getTechStackById } from '../utils/techStacks';
import TechStackSelector from '../components/TechStackSelector';

// Types d'applications
interface AppUpdate {
  timestamp: Date;
  description: string;
  version?: string;
}

interface PlatformDownload {
  platform: 'ios' | 'android' | 'windows' | 'linux' | 'web';
  downloadUrl: string;
  version: string;
  status: 'active' | 'deprecated' | 'beta' | 'development';
  history?: PlatformVersionHistory[];
  updatedAt?: Date;
}

interface PlatformVersionHistory {
  version: string;
  downloadUrl: string;
  status: 'active' | 'deprecated' | 'beta' | 'development';
  releaseDate: Date;
  notes?: string;
}

interface CustomerApp {
  id?: string;
  name: string;
  description: string;
  status: 'active' | 'inactive' | 'development' | 'maintenance';
  link: string;
  updates: AppUpdate[];
  createdAt?: Date;
  updatedAt?: Date;
  category?: 'webApp' | 'mobileApp' | 'desktop' | 'api' | 'other';
  techStack?: string;
  repository?: string;
  docsLink?: string;
  stage?: 'planning' | 'development' | 'testing' | 'production' | 'deprecated';
  urlApp?: string;
  downloads?: PlatformDownload[];
  stacks?: string[]; // IDs des stacks technologiques
}

interface Customer {
  id: string;
  customerId: string;
  name: string;
  companyName: string;
  status: string;
}

const MyhubAdmin: React.FC = () => {
  console.log('MyhubAdmin: Component starting...');
  
  const { t } = useLanguage();
  console.log('MyhubAdmin: useLanguage hook called');
  
  const { currentUser, loading: authLoading } = useAuth();
  console.log('MyhubAdmin: useAuth hook called, currentUser:', currentUser, 'authLoading:', authLoading);
  
  const [tabValue, setTabValue] = useState(0);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [apps, setApps] = useState<CustomerApp[]>([]);
  const [selectedCustomerId, setSelectedCustomerId] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [loadingApps, setLoadingApps] = useState(false);
  const [isMounted, setIsMounted] = useState(true);
  
  console.log('MyhubAdmin: State initialized');
  
  // État pour le formulaire de nouvelle application
  const [newApp, setNewApp] = useState<CustomerApp>({
    name: '',
    description: '',
    status: 'development',
    link: '',
    updates: [],
    downloads: [],
    stacks: []
  });
  
  // État pour le formulaire de mise à jour
  const [newUpdate, setNewUpdate] = useState<AppUpdate>({
    timestamp: new Date(),
    description: '',
    version: ''
  });
  
  // État pour l'édition d'une application
  const [editingApp, setEditingApp] = useState<CustomerApp | null>(null);
  const [isEditMode, setIsEditMode] = useState(false);
  
  // État pour les dialogues
  const [openAppDialog, setOpenAppDialog] = useState(false);
  const [openUpdateDialog, setOpenUpdateDialog] = useState(false);
  const [openHistoryDialog, setOpenHistoryDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openPlatformDialog, setOpenPlatformDialog] = useState(false);
  const [openPlatformHistoryDialog, setOpenPlatformHistoryDialog] = useState(false);
  const [currentAppId, setCurrentAppId] = useState<string>('');
  const [currentPlatformIndex, setCurrentPlatformIndex] = useState<number>(-1);
  
  // État pour l'édition d'une plateforme
  const [editingPlatform, setEditingPlatform] = useState<PlatformDownload | null>(null);
  
  // État pour l'ajout d'une nouvelle note de version
  const [newVersionNote, setNewVersionNote] = useState<string>('');
  
  // État pour les notifications
  const [notification, setNotification] = useState({
    show: false,
    message: '',
    type: 'success' as 'success' | 'error'
  });

  // État pour les onglets du dialog
  const [dialogTabValue, setDialogTabValue] = useState(0);

  // État pour l'ajout d'une nouvelle plateforme
  const [newPlatform, setNewPlatform] = useState<PlatformDownload>({
    platform: 'android',
    downloadUrl: '',
    version: '',
    status: 'development'
  });

  console.log('MyhubAdmin: All state initialized');

  // Afficher une notification
  const showNotification = (message: string, type: 'success' | 'error') => {
    setNotification({
      show: true,
      message,
      type
    });
    
    // Masquer la notification après 5 secondes
    setTimeout(() => {
      setNotification(prev => ({ ...prev, show: false }));
    }, 5000);
  };

  console.log('MyhubAdmin: showNotification function defined');

  // Gérer le montage/démontage du composant
  useEffect(() => {
    console.log('MyhubAdmin: useEffect for isMounted called');
    setIsMounted(true);
    return () => {
      setIsMounted(false);
    };
  }, []);

  // Charger la liste des clients
  useEffect(() => {
    const fetchCustomers = async () => {
      if (!isMounted || !currentUser) return;
      
      setLoading(true);
      try {
        const customersCollection = collection(db, 'customers');
        const customersSnapshot = await getDocs(customersCollection);
        const customersList = customersSnapshot.docs
          .map(doc => ({
            id: doc.id,
            ...doc.data()
          } as Customer))
          .filter(customer => customer.status === 'active')
          .sort((a, b) => a.companyName.localeCompare(b.companyName));
        
        if (isMounted) {
          setCustomers(customersList);
        }
      } catch (error) {
        console.error('Error fetching customers:', error);
        if (isMounted && t) {
          showNotification(t('admin.applications.notifications.errorLoadingCustomers') as string, 'error');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchCustomers();
  }, [isMounted, t, currentUser]);

  // Charger les applications du client sélectionné
  useEffect(() => {
    if (!isMounted || !currentUser) return;
    
    if (selectedCustomerId) {
      // Ajouter un délai pour éviter les appels trop fréquents
      const timeoutId = setTimeout(() => {
        if (isMounted) {
          fetchCustomerApps(selectedCustomerId);
        }
      }, 100);
      
      return () => clearTimeout(timeoutId);
    } else {
      setApps([]);
      setLoadingApps(false);
    }
  }, [selectedCustomerId, t, isMounted, currentUser]);

  console.log('MyhubAdmin: About to check authentication...');

  // Vérifier l'authentification
  if (authLoading) {
    console.log('MyhubAdmin: Auth loading...');
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (!currentUser) {
    console.log('MyhubAdmin: No current user');
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Paper elevation={0} sx={{ p: 4, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
          <Typography variant="h5" gutterBottom>
            {t('admin.applications.auth.required') || 'Authentification requise'}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {t('admin.applications.auth.pleaseLogin') || 'Veuillez vous connecter pour accéder à cette page.'}
          </Typography>
        </Paper>
      </Container>
    );
  }

  console.log('MyhubAdmin: User authenticated, rendering main content');

  // Récupérer les applications d'un client
  const fetchCustomerApps = async (customerId: string) => {
    if (!customerId || !isMounted || !currentUser) {
      if (isMounted) {
        setLoadingApps(false);
      }
      return;
    }
    
    if (isMounted) {
      setLoadingApps(true);
    }
    
    try {
      const appsCollection = collection(db, `customers/${customerId}/apps`);
      const appsQuery = query(appsCollection, orderBy('name'));
      const appsSnapshot = await getDocs(appsQuery);
      
      const appsList = appsSnapshot.docs.map(doc => {
        const data = doc.data();
        
        // Convertir les dates ISO en objets Date
        const convertISODatesToDate = (obj: any): any => {
          if (!obj) return obj;
          
          if (typeof obj === 'string' && obj.match(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/)) {
            return new Date(obj);
          }
          
          if (Array.isArray(obj)) {
            return obj.map(item => convertISODatesToDate(item));
          }
          
          if (typeof obj === 'object' && obj !== null) {
            const result: any = {};
            for (const [key, value] of Object.entries(obj)) {
              result[key] = convertISODatesToDate(value);
            }
            return result;
          }
          
          return obj;
        };
        
        // Traiter spécifiquement les dates des téléchargements
        const downloads = data.downloads?.map((download: any) => ({
          ...download,
          updatedAt: download.updatedAt ? new Date(download.updatedAt) : undefined,
          history: download.history?.map((version: any) => ({
            ...version,
            releaseDate: version.releaseDate ? new Date(version.releaseDate) : undefined
          }))
        }));
        
        // Traiter les mises à jour
        const updates = data.updates?.map((update: any) => ({
          ...update,
          timestamp: update.timestamp?.toDate ? update.timestamp.toDate() : 
                    (typeof update.timestamp === 'string' ? new Date(update.timestamp) : new Date())
        }));
        
        return {
          id: doc.id,
          ...data,
          downloads,
          updates,
          createdAt: data.createdAt?.toDate?.() || undefined,
          updatedAt: data.updatedAt?.toDate?.() || undefined
        } as CustomerApp;
      });
      
      if (isMounted) {
        setApps(appsList);
      }
    } catch (error) {
      console.error('Error fetching customer apps:', error);
      if (isMounted) {
        setApps([]);
        try {
          if (t && typeof showNotification === 'function') {
            showNotification(t('admin.applications.notifications.errorLoadingApps') as string, 'error');
          }
        } catch (notificationError) {
          console.error('Error showing notification:', notificationError);
        }
      }
    } finally {
      if (isMounted) {
        setLoadingApps(false);
      }
    }
  };

  // Gérer le changement d'onglet
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  // Gérer la sélection du client
  const handleCustomerChange = (event: SelectChangeEvent) => {
    setSelectedCustomerId(event.target.value);
  };

  // Gérer le changement d'onglet dans le dialogue
  const handleDialogTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setDialogTabValue(newValue);
  };

  // Ouvrir le dialogue pour ajouter une nouvelle application
  const handleOpenAppDialog = () => {
    setIsEditMode(false);
    setDialogTabValue(0);
    setNewApp({
      name: '',
      description: '',
      status: 'development',
      link: '',
      updates: [],
      category: 'webApp',
      techStack: '',
      repository: '',
      docsLink: '',
      stage: 'planning',
      urlApp: '',
      downloads: [],
      stacks: []
    });
    setOpenAppDialog(true);
  };

  // Ouvrir le dialogue pour éditer une application
  const handleOpenEditAppDialog = (app: CustomerApp) => {
    setIsEditMode(true);
    setDialogTabValue(0);
    setEditingApp(app);
    setNewApp({ 
      ...app,
      category: app.category || 'webApp',
      stage: app.stage || 'development'
    });
    setOpenAppDialog(true);
  };

  // Ouvrir le dialogue pour ajouter une mise à jour
  const handleOpenUpdateDialog = (app: CustomerApp) => {
    setEditingApp(app);
    setNewUpdate({
      version: '',
      description: '',
      timestamp: new Date()
    });
    setOpenUpdateDialog(true);
  };

  // Ouvrir le dialogue pour voir l'historique des mises à jour
  const handleOpenHistoryDialog = (app: CustomerApp) => {
    setEditingApp(app);
    setOpenHistoryDialog(true);
  };

  // Ouvrir le dialogue de confirmation de suppression
  const handleOpenDeleteDialog = (app: CustomerApp) => {
    setEditingApp(app);
    setOpenDeleteDialog(true);
  };

  // Fermer tous les dialogues
  const handleCloseDialogs = () => {
    setOpenAppDialog(false);
    setOpenUpdateDialog(false);
    setOpenHistoryDialog(false);
    setOpenDeleteDialog(false);
    setDialogTabValue(0);
    setCurrentAppId('');
  };

  // Mettre à jour les champs du formulaire d'application
  const handleAppFormChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent
  ) => {
    const name = event.target.name as keyof CustomerApp;
    const value = event.target.value;
    
    setNewApp(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Mettre à jour les champs du formulaire de mise à jour
  const handleUpdateFormChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    
    setNewUpdate(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Mettre à jour les champs du formulaire de plateforme
  const handlePlatformInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    
    setNewPlatform(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  // Gérer le changement de sélection pour la plateforme
  const handlePlatformSelectChange = (event: SelectChangeEvent<string>) => {
    const { name, value } = event.target;
    
    setNewPlatform(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Gérer la sélection des stacks technologiques
  const handleStacksChange = (stacks: string[]) => {
    setNewApp(prev => ({
      ...prev,
      stacks
    }));
  };

  // Fonction pour formater une date en toLocaleDateString de manière sécurisée
  const formatDate = (date: any): string => {
    if (!date) return '';
    
    try {
      // Si c'est déjà une Date
      if (date instanceof Date) {
        return date.toLocaleDateString();
      }
      
      // Si c'est une chaîne, essayer de la convertir
      if (typeof date === 'string') {
        return new Date(date).toLocaleDateString();
      }
      
      // Si c'est un timestamp Firebase
      if (date.toDate && typeof date.toDate === 'function') {
        return date.toDate().toLocaleDateString();
      }
      
      return '';
    } catch (error) {
      console.error('Error formatting date:', error, date);
      return '';
    }
  };

  // Fonction pour convertir une date en ISO String de manière sécurisée
  const toISOStringOrNull = (date: any): string | null => {
    if (!date) return null;
    
    try {
      // Si c'est déjà une Date
      if (date instanceof Date) {
        // Vérifier si la date est valide
        if (isNaN(date.getTime())) {
          return null;
        }
        return date.toISOString();
      }
      
      // Si c'est une chaîne, essayer de la convertir
      if (typeof date === 'string') {
        const dateObj = new Date(date);
        if (isNaN(dateObj.getTime())) {
          return null;
        }
        return dateObj.toISOString();
      }
      
      return null;
    } catch (error) {
      console.error('Error converting date to ISO:', error, date);
      return null;
    }
  };

  // Sauvegarder une nouvelle application ou mettre à jour une existante
  const handleSaveApp = async () => {
    if (!selectedCustomerId) return;
    
    try {
      setLoading(true);
      
      // Fonction pour nettoyer les objets avant sauvegarde dans Firebase
      const cleanUndefinedValues = (obj: any): any => {
        // Si c'est un tableau, nettoyer chaque élément
        if (Array.isArray(obj)) {
          return obj.map(item => 
            typeof item === 'object' && item !== null 
              ? cleanUndefinedValues(item) 
              : item ?? null
          );
        }
        
        // Si c'est un objet, nettoyer ses propriétés
        if (typeof obj === 'object' && obj !== null) {
          const cleanedObj: any = {};
          
          for (const [key, value] of Object.entries(obj)) {
            // Ignorer les propriétés undefined
            if (value === undefined) continue;
            
            // Nettoyer récursivement les objets imbriqués
            if (typeof value === 'object' && value !== null) {
              cleanedObj[key] = cleanUndefinedValues(value);
            } else {
              cleanedObj[key] = value;
            }
          }
          
          return cleanedObj;
        }
        
        return obj ?? null; // Remplacer undefined par null si nécessaire
      };
      
      // Préparer les données à sauvegarder
      const appData = cleanUndefinedValues({
        ...newApp,
        // Convertir les dates des historiques de version pour Firebase
        downloads: newApp.downloads?.map(download => {
          // Créer une copie sans les dates qui seront remplacées
          const { updatedAt, ...downloadWithoutDates } = download;
          
          return {
            ...downloadWithoutDates,
            updatedAt: toISOStringOrNull(download.updatedAt) || new Date().toISOString(),
            history: download.history?.map(version => {
              const { releaseDate, ...versionWithoutDate } = version;
              return {
                ...versionWithoutDate,
                releaseDate: toISOStringOrNull(version.releaseDate) || new Date().toISOString()
              };
            })
          };
        }),
        // S'assurer que techStack n'est pas undefined
        techStack: newApp.techStack || '',
        // S'assurer que stacks est un tableau
        stacks: Array.isArray(newApp.stacks) ? newApp.stacks : [],
        updatedAt: serverTimestamp()
      });
      
      console.log('Saving app data:', appData);
      
      if (isEditMode && editingApp?.id) {
        // Mise à jour d'une application existante
        const appRef = doc(db, `customers/${selectedCustomerId}/apps/${editingApp.id}`);
        
        await updateDoc(appRef, appData);
        
        showNotification(t('admin.applications.notifications.updated') as string, 'success');
      } else {
        // Création d'une nouvelle application
        const appsCollection = collection(db, `customers/${selectedCustomerId}/apps`);
        
        await addDoc(appsCollection, {
          ...appData,
          createdAt: serverTimestamp()
        });
        
        showNotification(t('admin.applications.notifications.created') as string, 'success');
      }
      
      // Rafraîchir la liste des applications
      await fetchCustomerApps(selectedCustomerId);
      handleCloseDialogs();
      
    } catch (error) {
      console.error('Error saving app:', error);
      showNotification(t('admin.applications.notifications.errorSaving') as string, 'error');
    } finally {
      setLoading(false);
    }
  };

  // Ajouter une mise à jour à une application
  const handleAddUpdate = async () => {
    if (!editingApp || !newUpdate.version || !newUpdate.description) {
      return;
    }
    
    try {
      // Trouver l'application à mettre à jour
      const appIndex = apps.findIndex(app => app.id === editingApp.id);
      if (appIndex === -1) return;
      
      // Créer une copie des applications
      const updatedApps = [...apps];
      
      // Ajouter la mise à jour à l'application
      updatedApps[appIndex] = {
        ...updatedApps[appIndex],
        updates: [
          ...updatedApps[appIndex].updates || [],
          newUpdate
        ]
      };
      
      // Mettre à jour l'état local
      setApps(updatedApps);
      
      // Fermer le dialogue
      setOpenUpdateDialog(false);
      
      // Afficher une notification de succès
      showNotification(t('admin.applications.notifications.updateAdded') as string, 'success');
    } catch (error) {
      console.error('Error adding update:', error);
      showNotification(t('admin.applications.notifications.errorAddingUpdate') as string, 'error');
    }
  };

  // Ajouter une plateforme de téléchargement
  const handleAddPlatform = () => {
    if (!newPlatform.downloadUrl || !newPlatform.version) {
      return;
    }
    
    // Ajouter l'historique initial
    const platformWithHistory: PlatformDownload = {
      ...newPlatform,
      updatedAt: new Date(),
      history: [{
        version: newPlatform.version,
        downloadUrl: newPlatform.downloadUrl,
        status: newPlatform.status,
        releaseDate: new Date(),
        notes: newVersionNote
      }]
    };
    
    setNewApp(prev => ({
      ...prev,
      downloads: [...(prev.downloads || []), platformWithHistory]
    }));
    
    // Réinitialiser le formulaire de plateforme
    setNewPlatform({
      platform: 'android',
      downloadUrl: '',
      version: '',
      status: 'development'
    });
    
    // Réinitialiser la note de version
    setNewVersionNote('');
  };

  // Supprimer une plateforme de téléchargement
  const handleRemovePlatform = (index: number) => {
    setNewApp(prev => ({
      ...prev,
      downloads: (prev.downloads || []).filter((_, i) => i !== index)
    }));
  };

  // Supprimer une application
  const handleDeleteApp = async () => {
    if (!editingApp) {
      return;
    }
    
    try {
      // Filtrer l'application à supprimer de la liste
      const updatedApps = apps.filter(app => app.id !== editingApp.id);
      
      // Mettre à jour l'état local
      setApps(updatedApps);
      
      // Fermer le dialogue
      setOpenDeleteDialog(false);
      
      // Afficher une notification de succès
      showNotification(t('admin.applications.notifications.deleted') as string, 'success');
    } catch (error) {
      console.error('Error deleting app:', error);
      showNotification(t('admin.applications.notifications.errorDeleting') as string, 'error');
    }
  };

  // Traduire le statut de l'application
  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'active':
        return t('admin.applications.status.active');
      case 'inactive':
        return t('admin.applications.status.inactive');
      case 'development':
        return t('admin.applications.status.development');
      case 'maintenance':
        return t('admin.applications.status.maintenance');
      default:
        return status;
    }
  };

  // Obtenir la couleur du statut
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'success';
      case 'inactive':
        return 'error';
      case 'development':
        return 'info';
      case 'maintenance':
        return 'warning';
      default:
        return 'default';
    }
  };

  // Obtenir le logo de la plateforme
  const getPlatformLogo = (platform: string) => {
    switch (platform) {
      case 'ios':
        return 'https://developer.apple.com/assets/elements/badges/download-on-the-app-store.svg';
      case 'android':
        return 'https://www.innovados.com/wp-content/uploads/google-play-logo-nuevo.png';
      case 'windows':
        return 'https://msftstories.thesourcemediaassets.com/sites/113/2017/05/Windows-10-Logo.png';
      case 'linux':
        return 'https://logodownload.org/wp-content/uploads/2022/05/linux-logo-2.png';
      case 'web':
        return 'https://cdn-icons-png.flaticon.com/512/2301/2301129.png';
      default:
        return '';
    }
  };

  // Obtenir le nom de la plateforme
  const getPlatformName = (platform: string) => {
    switch (platform) {
      case 'ios':
        return 'iOS (App Store)';
      case 'android':
        return 'Android (Google Play)';
      case 'windows':
        return 'Windows';
      case 'linux':
        return 'Linux';
      case 'web':
        return 'Web App';
      default:
        return platform;
    }
  };

  // Obtenir la couleur de l'état de la plateforme
  const getPlatformStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'success';
      case 'deprecated':
        return 'error';
      case 'beta':
        return 'warning';
      case 'development':
        return 'info';
      default:
        return 'default';
    }
  };
  
  // Obtenir le libellé de l'état de la plateforme
  const getPlatformStatusLabel = (status: string) => {
    switch (status) {
      case 'active':
        return t('admin.applications.platformStatus.active') || 'Active';
      case 'deprecated':
        return t('admin.applications.platformStatus.deprecated') || 'Deprecated';
      case 'beta':
        return t('admin.applications.platformStatus.beta') || 'Beta';
      case 'development':
        return t('admin.applications.platformStatus.development') || 'Development';
      default:
        return status;
    }
  };

  // Ajouter cette fonction pour afficher les plateformes disponibles dans la liste des applications
  const renderPlatformIcons = (downloads?: PlatformDownload[]) => {
    if (!downloads || downloads.length === 0) return null;
    
    return (
      <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
        {downloads.map((download, index) => (
          <Tooltip 
            key={index} 
            title={
              <>
                {getPlatformName(download.platform)} v{download.version}
                <br />
                {getPlatformStatusLabel(download.status)}
              </>
            }
          >
            <Box
              component="a"
              href={download.downloadUrl}
              target="_blank"
              rel="noopener noreferrer"
              sx={{ 
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 32,
                height: 32,
                borderRadius: '50%',
                bgcolor: 'background.paper',
                border: '1px solid',
                borderColor: 'divider',
                overflow: 'hidden',
                position: 'relative',
                '&:hover': {
                  boxShadow: 1
                }
              }}
            >
              <Box
                component="img"
                src={getPlatformLogo(download.platform)}
                alt={download.platform}
                sx={{
                  width: 20,
                  height: 20,
                  objectFit: 'contain'
                }}
              />
              <Box
                sx={{
                  position: 'absolute',
                  bottom: 0,
                  right: 0,
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  bgcolor: `${getPlatformStatusColor(download.status)}.main`,
                  border: '1px solid white'
                }}
              />
            </Box>
          </Tooltip>
        ))}
      </Box>
    );
  };

  // Ouvrir le dialogue d'édition d'une plateforme
  const handleOpenPlatformDialog = (platform: PlatformDownload, index: number) => {
    setEditingPlatform(platform);
    setCurrentPlatformIndex(index);
    setNewPlatform({
      ...platform
    });
    setNewVersionNote('');
    setOpenPlatformDialog(true);
  };
  
  // Ouvrir le dialogue d'historique d'une plateforme
  const handleOpenPlatformHistoryDialog = (platform: PlatformDownload) => {
    setEditingPlatform(platform);
    setOpenPlatformHistoryDialog(true);
  };
  
  // Mettre à jour une plateforme existante
  const handleUpdatePlatform = () => {
    if (!editingPlatform || currentPlatformIndex === -1) {
      return;
    }
    
    // Vérifier si la version a changé
    const versionChanged = editingPlatform.version !== newPlatform.version ||
                          editingPlatform.downloadUrl !== newPlatform.downloadUrl ||
                          editingPlatform.status !== newPlatform.status;
    
    // Créer une copie de la plateforme avec les nouvelles valeurs
    const updatedPlatform: PlatformDownload = {
      ...newPlatform,
      updatedAt: new Date()
    };
    
    // Si la version a changé, ajouter à l'historique
    if (versionChanged) {
      const newHistoryEntry = {
        version: newPlatform.version,
        downloadUrl: newPlatform.downloadUrl,
        status: newPlatform.status,
        releaseDate: new Date(),
        notes: newVersionNote
      };
      
      updatedPlatform.history = [
        newHistoryEntry,
        ...(editingPlatform.history || [])
      ];
    } else {
      updatedPlatform.history = editingPlatform.history || [];
    }
    
    // Mettre à jour l'application
    setNewApp(prev => {
      const updatedDownloads = [...(prev.downloads || [])];
      updatedDownloads[currentPlatformIndex] = updatedPlatform;
      
      return {
        ...prev,
        downloads: updatedDownloads
      };
    });
    
    // Fermer le dialogue
    setOpenPlatformDialog(false);
    setCurrentPlatformIndex(-1);
    setEditingPlatform(null);
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Notification */}
      {notification.show && (
        <Alert 
          severity={notification.type} 
          sx={{ mb: 2 }}
          onClose={() => setNotification(prev => ({ ...prev, show: false }))}
        >
          {notification.message}
        </Alert>
      )}
      
      {/* En-tête */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AppsIcon fontSize="large" />
          {t('admin.applications.title')}
        </Typography>
        <Typography variant="subtitle1" color="text.secondary">
          {t('admin.applications.subtitle')}
        </Typography>
      </Box>
      
      {/* Sélecteur de client */}
      <Paper elevation={0} sx={{ p: 3, mb: 3, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
        <FormControl fullWidth variant="outlined">
          <InputLabel id="customer-select-label">{t('admin.applications.selectCustomer')}</InputLabel>
          <Select
            labelId="customer-select-label"
            id="customer-select"
            value={selectedCustomerId}
            onChange={handleCustomerChange}
            label={t('admin.applications.selectCustomer')}
            disabled={loading}
          >
            <MenuItem value="">
              <em>{t('admin.applications.selectCustomer')}</em>
            </MenuItem>
            {customers.map((customer) => (
              <MenuItem key={customer.id} value={customer.id}>
                {customer.companyName} ({customer.customerId})
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Paper>
      
      {/* Contenu principal */}
      {selectedCustomerId ? (
        <Paper elevation={0} sx={{ p: 4, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h5">
              {t('admin.applications.title')}
            </Typography>
            <Button 
              variant="contained" 
              startIcon={<AddIcon />}
              onClick={handleOpenAppDialog}
            >
              {t('admin.applications.button.add')}
            </Button>
          </Box>
          
          <Tabs value={tabValue} onChange={handleTabChange} sx={{ mb: 2 }}>
            <Tab label={t('admin.applications.tabs.list')} id="tab-0" aria-controls="tabpanel-0" />
            <Tab label={t('admin.applications.tabs.statistics')} id="tab-1" aria-controls="tabpanel-1" />
          </Tabs>
          
          <Box
            role="tabpanel"
            hidden={tabValue !== 0}
            id="tabpanel-0"
            aria-labelledby="tab-0"
            sx={{ p: 3 }}
          >
            {tabValue === 0 && (
              <Stack spacing={3}>
                {loadingApps ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%', p: 4 }}>
                    <CircularProgress />
                  </Box>
                ) : apps.length > 0 ? (
                  apps.map((app) => (
                    <Card key={app.id} variant="outlined" sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                      <CardContent sx={{ flexGrow: 1 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                          <Typography variant="h6" component="div" gutterBottom>
                            {app.name}
                          </Typography>
                          <Chip 
                            label={getStatusLabel(app.status)} 
                            color={getStatusColor(app.status) as any}
                            size="small"
                          />
                        </Box>
                        
                        {app.urlApp && (
                          <Box sx={{ mb: 2, display: 'flex', justifyContent: 'center' }}>
                            <Box
                              component="img"
                              src={app.urlApp}
                              alt={app.name}
                              sx={{
                                maxWidth: '100%',
                                maxHeight: 150,
                                objectFit: 'contain',
                                borderRadius: 1
                              }}
                              onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                                e.currentTarget.style.display = 'none';
                              }}
                            />
                          </Box>
                        )}
                        
                        {app.category && (
                          <Chip 
                            label={t(`admin.applications.form.category.${app.category}`)} 
                            size="small" 
                            variant="outlined"
                            sx={{ mr: 1, mb: 1 }}
                          />
                        )}
                        
                        {app.stage && (
                          <Chip 
                            label={t(`admin.applications.form.stage.${app.stage}`)} 
                            size="small" 
                            variant="outlined"
                            sx={{ mb: 1 }}
                          />
                        )}
                        
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                          {app.description || t('admin.applications.noDescription')}
                        </Typography>
                        
                        {app.techStack && (
                          <Typography variant="body2" sx={{ mb: 1 }}>
                            <strong>{t('admin.applications.form.techStack')}:</strong> {app.techStack}
                          </Typography>
                        )}
                        
                        {app.link && (
                          <Typography variant="body2" sx={{ mb: 1, wordBreak: 'break-all' }}>
                            <strong>URL:</strong>{' '}
                            <a href={app.link} target="_blank" rel="noopener noreferrer">
                              {app.link}
                            </a>
                          </Typography>
                        )}
                        
                        {/* Afficher les plateformes disponibles */}
                        {app.downloads && app.downloads.length > 0 && (
                          <Box sx={{ mt: 2 }}>
                            <Typography variant="body2" sx={{ mb: 1 }}>
                              <strong>{t('admin.applications.form.downloads')}:</strong>
                            </Typography>
                            {renderPlatformIcons(app.downloads)}
                          </Box>
                        )}
                        
                        {app.updates && app.updates.length > 0 && (
                          <Typography variant="body2" sx={{ mt: 2 }}>
                            <strong>{t('admin.applications.lastUpdate')}:</strong>{' '}
                            {app.updates[0].version ? `v${app.updates[0].version}` : ''}
                            {' '}{formatDate(app.updates[0].timestamp)}
                          </Typography>
                        )}

                        {app.stacks && app.stacks.length > 0 && (
                          <Box sx={{ mt: 2, mb: 2 }}>
                            <Typography variant="body2" sx={{ mb: 1 }}>
                              <strong>{t('admin.applications.form.techStack')}:</strong>
                            </Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                              {app.stacks.map(stackId => {
                                const stack = getTechStackById(stackId);
                                if (!stack) return null;
                                
                                return (
                                  <Chip
                                    key={stackId}
                                    label={stack.name}
                                    size="small"
                                    sx={{ 
                                      bgcolor: `${stack.color}22`,
                                      borderColor: stack.color,
                                      border: '1px solid',
                                      fontSize: '0.7rem'
                                    }}
                                  />
                                );
                              })}
                            </Box>
                          </Box>
                        )}
                      </CardContent>
                      <CardActions>
                        <Box sx={{ mt: 1, display: 'flex', justifyContent: 'space-between' }}>
                          <Button 
                            size="small" 
                            variant="outlined" 
                            onClick={() => handleOpenUpdateDialog(app)}
                          >
                            {t('admin.applications.button.addUpdate')}
                          </Button>
                          <Button 
                            size="small" 
                            variant="outlined" 
                            onClick={() => handleOpenHistoryDialog(app)}
                          >
                            {t('admin.applications.button.history')}
                          </Button>
                          <Tooltip title={t('admin.applications.button.edit') as string}>
                            <IconButton size="small" onClick={() => handleOpenEditAppDialog(app)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={t('admin.applications.button.delete') as string}>
                            <IconButton size="small" color="error" onClick={() => handleOpenDeleteDialog(app)}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </CardActions>
                    </Card>
                  ))
                ) : (
                  <Box sx={{ p: 3, textAlign: 'center', width: '100%' }}>
                    <Typography variant="body1" color="text.secondary">
                      {t('admin.applications.noApps')}
                    </Typography>
                  </Box>
                )}
              </Stack>
            )}
          </Box>
          
          {/* Onglet Statistiques */}
          <Box
            role="tabpanel"
            hidden={tabValue !== 1}
            id="tabpanel-1"
            aria-labelledby="tab-1"
            sx={{ p: 3 }}
          >
            {tabValue === 1 && (
              <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography variant="h6" gutterBottom>
                  {t('admin.applications.statisticsSoon')}
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  {t('admin.applications.statisticsDescription')}
                </Typography>
              </Box>
            )}
          </Box>
        </Paper>
      ) : (
        <Paper elevation={0} sx={{ p: 4, borderRadius: 2, border: '1px solid', borderColor: 'divider', textAlign: 'center' }}>
          <Typography variant="h6" gutterBottom>
            {t('admin.applications.noCustomerSelected')}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {t('admin.applications.selectCustomerInstruction')}
          </Typography>
        </Paper>
      )}
      
      {/* Dialogue de création/édition d'application */}
      <Dialog open={openAppDialog} onClose={handleCloseDialogs} maxWidth="md" fullWidth>
        <DialogTitle>{isEditMode ? t('admin.applications.dialog.editApplication') : t('admin.applications.dialog.addApplication')}</DialogTitle>
        <DialogContent>
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
            <Tabs value={dialogTabValue} onChange={handleDialogTabChange}>
              <Tab label={t('admin.applications.form.tabs.basic')} />
              <Tab label={t('admin.applications.form.tabs.technical')} />
              <Tab label={t('admin.applications.form.tabs.access')} />
              <Tab label={t('admin.applications.form.tabs.provisioning')} />
            </Tabs>
          </Box>

          {/* Onglet 1: Informations de base */}
          <Box hidden={dialogTabValue !== 0} sx={{ pt: 1 }}>
            <Stack spacing={2}>
              <TextField
                name="name"
                label={t('admin.applications.form.name')}
                fullWidth
                required
                value={newApp.name}
                onChange={handleAppFormChange}
              />
              
              <TextField
                name="description"
                label={t('admin.applications.form.description')}
                fullWidth
                multiline
                rows={3}
                value={newApp.description}
                onChange={handleAppFormChange}
              />
              
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Box sx={{ flex: 1 }}>
                  <FormControl fullWidth>
                    <InputLabel>{t('admin.applications.form.status')}</InputLabel>
                    <Select
                      name="status"
                      value={newApp.status}
                      label={t('admin.applications.form.status')}
                      onChange={handleAppFormChange}
                    >
                      <MenuItem value="development">{t('admin.applications.status.development')}</MenuItem>
                      <MenuItem value="active">{t('admin.applications.status.active')}</MenuItem>
                      <MenuItem value="maintenance">{t('admin.applications.status.maintenance')}</MenuItem>
                      <MenuItem value="inactive">{t('admin.applications.status.inactive')}</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
                
                <Box sx={{ flex: 1 }}>
                  <FormControl fullWidth>
                    <InputLabel>{t('admin.applications.form.category')}</InputLabel>
                    <Select
                      name="category"
                      value={newApp.category || 'webApp'}
                      label={t('admin.applications.form.category')}
                      onChange={handleAppFormChange}
                    >
                      <MenuItem value="webApp">{t('admin.applications.form.category.webApp')}</MenuItem>
                      <MenuItem value="mobileApp">{t('admin.applications.form.category.mobileApp')}</MenuItem>
                      <MenuItem value="desktop">{t('admin.applications.form.category.desktop')}</MenuItem>
                      <MenuItem value="api">{t('admin.applications.form.category.api')}</MenuItem>
                      <MenuItem value="other">{t('admin.applications.form.category.other')}</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
              </Box>
            </Stack>
          </Box>

          {/* Onglet 2: Détails techniques */}
          <Box hidden={dialogTabValue !== 1} sx={{ pt: 1 }}>
            <Stack spacing={2}>
              <Typography variant="subtitle1" gutterBottom>
                {t('admin.applications.form.techStack')}
              </Typography>
              
              <TechStackSelector
                selectedStacks={newApp.stacks || []}
                onChange={handleStacksChange}
              />
              
              <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                <Box sx={{ flex: 1 }}>
                  <FormControl fullWidth>
                    <InputLabel>{t('admin.applications.form.stage')}</InputLabel>
                    <Select
                      name="stage"
                      value={newApp.stage || 'development'}
                      label={t('admin.applications.form.stage')}
                      onChange={handleAppFormChange}
                    >
                      <MenuItem value="planning">{t('admin.applications.form.stage.planning')}</MenuItem>
                      <MenuItem value="development">{t('admin.applications.form.stage.development')}</MenuItem>
                      <MenuItem value="testing">{t('admin.applications.form.stage.testing')}</MenuItem>
                      <MenuItem value="production">{t('admin.applications.form.stage.production')}</MenuItem>
                      <MenuItem value="deprecated">{t('admin.applications.form.stage.deprecated')}</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
              </Box>
            </Stack>
          </Box>

          {/* Onglet 3: Accès et documentation */}
          <Box hidden={dialogTabValue !== 2} sx={{ pt: 1 }}>
            <Stack spacing={2}>
              <TextField
                name="link"
                label={t('admin.applications.form.url')}
                fullWidth
                value={newApp.link}
                onChange={handleAppFormChange}
                placeholder="https://"
              />
              
              <TextField
                name="repository"
                label={t('admin.applications.form.repository')}
                fullWidth
                value={newApp.repository || ''}
                onChange={handleAppFormChange}
                placeholder={t('admin.applications.form.repository.placeholder') as string}
              />
              
              <TextField
                name="docsLink"
                label={t('admin.applications.form.docsLink')}
                fullWidth
                value={newApp.docsLink || ''}
                onChange={handleAppFormChange}
                placeholder={t('admin.applications.form.docsLink.placeholder') as string}
              />
              
              <TextField
                name="urlApp"
                label={t('admin.applications.form.urlApp')}
                fullWidth
                value={newApp.urlApp || ''}
                onChange={handleAppFormChange}
                placeholder={t('admin.applications.form.urlApp.placeholder') as string}
              />
            </Stack>
          </Box>

          {/* Onglet 4: Provisioning */}
          <Box hidden={dialogTabValue !== 3} sx={{ pt: 1 }}>
            <Typography variant="subtitle1" gutterBottom>
              {t('admin.applications.form.downloads')}
            </Typography>
            
            {/* Liste des plateformes existantes */}
            {newApp.downloads && newApp.downloads.length > 0 ? (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" gutterBottom color="text.secondary">
                  {t('admin.applications.form.currentDownloads')}
                </Typography>
                
                <Stack spacing={2}>
                  {newApp.downloads.map((download, index) => (
                    <Card key={index} variant="outlined" sx={{ p: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Box
                            component="img"
                            src={getPlatformLogo(download.platform)}
                            alt={download.platform}
                            sx={{ 
                              height: 40, 
                              width: 'auto', 
                              maxWidth: 120,
                              objectFit: 'contain'
                            }}
                          />
                          <Box>
                            <Typography variant="body1">
                              {getPlatformName(download.platform)}
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="caption" color="text.secondary">
                                Version {download.version}
                              </Typography>
                              <Chip 
                                label={getPlatformStatusLabel(download.status)} 
                                color={getPlatformStatusColor(download.status) as any}
                                size="small"
                                sx={{ height: 20 }}
                              />
                            </Box>
                          </Box>
                        </Box>
                        
                        <Box>
                          <Tooltip title={t('admin.applications.button.edit')}>
                            <IconButton 
                              size="small" 
                              onClick={() => handleOpenPlatformDialog(download, index)}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={t('admin.applications.button.history')}>
                            <IconButton 
                              size="small" 
                              onClick={() => handleOpenPlatformHistoryDialog(download)}
                            >
                              <HistoryIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={t('admin.applications.button.delete')}>
                            <IconButton 
                              size="small" 
                              color="error" 
                              onClick={() => handleRemovePlatform(index)}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </Box>
                      
                      <Typography 
                        variant="body2" 
                        sx={{ mt: 1, wordBreak: 'break-all' }}
                        color="primary"
                      >
                        <a href={download.downloadUrl} target="_blank" rel="noopener noreferrer">
                          {download.downloadUrl}
                        </a>
                      </Typography>
                      
                      {download.updatedAt && (
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                          {t('admin.applications.lastUpdated')}: {formatDate(download.updatedAt)}
                        </Typography>
                      )}
                    </Card>
                  ))}
                </Stack>
              </Box>
            ) : (
              <Box sx={{ mb: 3, p: 2, textAlign: 'center', bgcolor: 'background.paper', borderRadius: 1 }}>
                <Typography color="text.secondary">
                  {t('admin.applications.form.noDownloads')}
                </Typography>
              </Box>
            )}
            
            <Divider sx={{ my: 2 }} />
            
            {/* Formulaire d'ajout de plateforme */}
            <Typography variant="subtitle2" gutterBottom>
              {t('admin.applications.form.addDownload')}
            </Typography>
            
            <Stack spacing={2} sx={{ mt: 1 }}>
              <FormControl fullWidth>
                <InputLabel>{t('admin.applications.form.platform')}</InputLabel>
                <Select
                  name="platform"
                  value={newPlatform.platform}
                  label={t('admin.applications.form.platform')}
                  onChange={handlePlatformSelectChange}
                >
                  <MenuItem value="android">Android (Google Play)</MenuItem>
                  <MenuItem value="ios">iOS (App Store)</MenuItem>
                  <MenuItem value="windows">Windows</MenuItem>
                  <MenuItem value="linux">Linux</MenuItem>
                  <MenuItem value="web">Web App</MenuItem>
                </Select>
              </FormControl>
              
              <TextField
                name="version"
                label={t('admin.applications.form.version')}
                fullWidth
                value={newPlatform.version}
                onChange={handlePlatformInputChange}
                placeholder="1.0.0"
              />
              
              <TextField
                name="downloadUrl"
                label={t('admin.applications.form.downloadUrl')}
                fullWidth
                value={newPlatform.downloadUrl}
                onChange={handlePlatformInputChange}
                placeholder="https://"
              />
              
              <FormControl fullWidth>
                <InputLabel>{t('admin.applications.form.platformStatus')}</InputLabel>
                <Select
                  name="status"
                  value={newPlatform.status}
                  label={t('admin.applications.form.platformStatus')}
                  onChange={handlePlatformSelectChange}
                >
                  <MenuItem value="active">{t('admin.applications.platformStatus.active')}</MenuItem>
                  <MenuItem value="beta">{t('admin.applications.platformStatus.beta')}</MenuItem>
                  <MenuItem value="development">{t('admin.applications.platformStatus.development')}</MenuItem>
                  <MenuItem value="deprecated">{t('admin.applications.platformStatus.deprecated')}</MenuItem>
                </Select>
              </FormControl>
              
              <TextField
                name="notes"
                label={t('admin.applications.form.versionNotes')}
                fullWidth
                multiline
                rows={2}
                value={newVersionNote}
                onChange={(e) => setNewVersionNote(e.target.value)}
                placeholder={t('admin.applications.form.versionNotesPlaceholder') as string}
                helperText={t('admin.applications.form.versionNotesHelp')}
              />
              
              <Button 
                variant="outlined" 
                startIcon={<AddIcon />}
                onClick={handleAddPlatform}
                disabled={!newPlatform.downloadUrl || !newPlatform.version}
              >
                {t('admin.applications.button.addPlatform')}
              </Button>
            </Stack>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialogs}>{t('admin.applications.button.cancel')}</Button>
          <Button 
            onClick={handleSaveApp}
            variant="contained"
            disabled={!newApp.name}
          >
            {isEditMode ? t('admin.applications.button.update') : t('admin.applications.button.create')}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Dialogue d'ajout de mise à jour */}
      <Dialog open={openUpdateDialog} onClose={handleCloseDialogs}>
        <DialogTitle>{t('admin.applications.dialog.addUpdate')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 0.5 }}>
            <TextField
              name="version"
              label={t('admin.applications.form.version')}
              fullWidth
              value={newUpdate.version}
              onChange={handleUpdateFormChange}
              placeholder="1.0.0"
            />
            
            <TextField
              name="description"
              label={t('admin.applications.form.updateDescription')}
              fullWidth
              multiline
              rows={3}
              required
              value={newUpdate.description}
              onChange={handleUpdateFormChange}
              placeholder={t('admin.applications.form.updatePlaceholder') as string}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialogs}>{t('admin.applications.button.cancel')}</Button>
          <Button 
            onClick={handleAddUpdate}
            variant="contained"
            disabled={!newUpdate.description}
          >
            {t('admin.applications.button.addUpdate')}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Dialogue d'historique des mises à jour */}
      <Dialog open={openHistoryDialog} onClose={handleCloseDialogs} maxWidth="md" fullWidth>
        <DialogTitle>{t('admin.applications.dialog.updateHistory')} - {editingApp?.name}</DialogTitle>
        <DialogContent dividers>
          {editingApp?.updates && editingApp.updates.length > 0 ? (
            <List>
              {editingApp.updates.map((update, index) => (
                <React.Fragment key={index}>
                  <ListItem alignItems="flex-start">
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="subtitle1">
                            {update.version ? `Version ${update.version}` : 'Update'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {formatDate(update.timestamp)} {update.timestamp instanceof Date ? update.timestamp.toLocaleTimeString() : ''}
                          </Typography>
                        </Box>
                      }
                      secondary={update.description}
                    />
                  </ListItem>
                  {index < editingApp.updates.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          ) : (
            <Box sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="body1" color="text.secondary">
                {t('admin.applications.noUpdatesRecorded')}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialogs}>{t('admin.applications.button.close')}</Button>
        </DialogActions>
      </Dialog>
      
      {/* Dialogue de confirmation de suppression */}
      <Dialog open={openDeleteDialog} onClose={handleCloseDialogs}>
        <DialogTitle>{t('admin.applications.dialog.deleteApplication')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('admin.applications.dialog.deleteConfirmation')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialogs}>{t('admin.applications.button.cancel')}</Button>
          <Button onClick={handleDeleteApp} color="error" variant="contained">
            {t('admin.applications.button.delete')}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Dialogue d'édition d'une plateforme */}
      <Dialog open={openPlatformDialog} onClose={() => setOpenPlatformDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('admin.applications.dialog.editPlatform')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant="subtitle2">
              {getPlatformName(newPlatform.platform)}
            </Typography>
            
            <TextField
              name="version"
              label={t('admin.applications.form.version')}
              fullWidth
              value={newPlatform.version}
              onChange={handlePlatformInputChange}
              placeholder="1.0.0"
            />
            
            <TextField
              name="downloadUrl"
              label={t('admin.applications.form.downloadUrl')}
              fullWidth
              value={newPlatform.downloadUrl}
              onChange={handlePlatformInputChange}
              placeholder="https://"
            />
            
            <FormControl fullWidth>
              <InputLabel>{t('admin.applications.form.platformStatus')}</InputLabel>
              <Select
                name="status"
                value={newPlatform.status}
                label={t('admin.applications.form.platformStatus')}
                onChange={handlePlatformSelectChange}
              >
                <MenuItem value="active">{t('admin.applications.platformStatus.active')}</MenuItem>
                <MenuItem value="beta">{t('admin.applications.platformStatus.beta')}</MenuItem>
                <MenuItem value="development">{t('admin.applications.platformStatus.development')}</MenuItem>
                <MenuItem value="deprecated">{t('admin.applications.platformStatus.deprecated')}</MenuItem>
              </Select>
            </FormControl>
            
            <TextField
              name="notes"
              label={t('admin.applications.form.versionNotes')}
              fullWidth
              multiline
              rows={2}
              value={newVersionNote}
              onChange={(e) => setNewVersionNote(e.target.value)}
              placeholder={t('admin.applications.form.versionNotesPlaceholder') as string}
              helperText={t('admin.applications.form.versionNotesHelp')}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenPlatformDialog(false)}>{t('admin.applications.button.cancel')}</Button>
          <Button 
            onClick={handleUpdatePlatform}
            variant="contained"
            disabled={!newPlatform.downloadUrl || !newPlatform.version}
          >
            {t('admin.applications.button.update')}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Dialogue d'historique des versions d'une plateforme */}
      <Dialog open={openPlatformHistoryDialog} onClose={() => setOpenPlatformHistoryDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {t('admin.applications.dialog.platformVersionHistory')} - {editingPlatform && getPlatformName(editingPlatform.platform)}
        </DialogTitle>
        <DialogContent dividers>
          {editingPlatform?.history && editingPlatform.history.length > 0 ? (
            <List>
              {editingPlatform.history.map((version, index) => (
                <React.Fragment key={index}>
                  <ListItem alignItems="flex-start">
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="subtitle1">
                            Version {version.version}
                          </Typography>
                          <Chip 
                            label={getPlatformStatusLabel(version.status)} 
                            color={getPlatformStatusColor(version.status) as any}
                            size="small"
                          />
                          <Typography variant="caption" color="text.secondary">
                            {formatDate(version.releaseDate)}
                          </Typography>
                        </Box>
                      }
                      secondary={
                        <>
                          <Typography variant="body2" component="span" color="text.primary" sx={{ display: 'block', mt: 1 }}>
                            <a href={version.downloadUrl} target="_blank" rel="noopener noreferrer">
                              {version.downloadUrl}
                            </a>
                          </Typography>
                          {version.notes && (
                            <Typography variant="body2" sx={{ mt: 1 }}>
                              {version.notes}
                            </Typography>
                          )}
                        </>
                      }
                    />
                  </ListItem>
                  {editingPlatform.history && index < editingPlatform.history.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          ) : (
            <Box sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="body1" color="text.secondary">
                {t('admin.applications.noVersionHistory')}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenPlatformHistoryDialog(false)}>{t('admin.applications.button.close')}</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default MyhubAdmin; 